import React, { useMemo } from 'react';
import { LineChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV } from '../../utils/exportToCSV';

/**
 * Line Widget Component
 * Modernized replacement for LineList.js
 * Displays time-series data as line chart or table
 */
function LineWidget({ data }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const dataset = apiData[0].columnDataSet1?.[0];
            if (!dataset || !dataset.labels || !dataset.data) return null;

            return {
                labels: dataset.labels,
                datasets: [{
                    label: data.YlabelString || 'Value',
                    data: dataset.data,
                    borderColor: data.ColorCode || '#f19f57',
                    backgroundColor: `${data.ColorCode || '#f19f57'}33`, // 20% opacity
                    tension: 0.4,
                    fill: true,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }]
            };
        } catch (err) {
            console.error('Error transforming line chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform API data to table format
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => ({
            date: label,
            value: chartData.datasets[0].data[index]
        }));
    }, [chartData]);

    // Table columns configuration
    const columns = [
        {
            key: 'date',
            label: data.XlabelString || 'Date',
            sortable: true
        },
        {
            key: 'value',
            label: data.YlabelString || 'Value',
            sortable: true,
            format: (val) => {
                if (typeof val === 'number') {
                    // Format as currency if it looks like a dollar amount
                    if (data.YlabelString?.includes('$') || data.title?.includes('Savings')) {
                        return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    }
                    // Format as percentage if applicable
                    if (data.YlabelString?.includes('%') || data.title?.includes('(%)')) {
                        return `${val.toFixed(2)}%`;
                    }
                    return val.toLocaleString();
                }
                return val;
            }
        }
    ];

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'line-chart-data');
        }
    };

    // Chart options
    const chartOptions = {
        scales: {
            x: {
                title: {
                    display: true,
                    text: data.XlabelString || ''
                }
            },
            y: {
                title: {
                    display: true,
                    text: data.YlabelString || ''
                },
                beginAtZero: true
            }
        },
        plugins: {
            legend: {
                display: true
            }
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <LineChart
                        data={chartData}
                        options={chartOptions}
                        height={350}
                    />
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 10 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default LineWidget;
